import type { Collection } from '../actions/collection';
import type { UriTemplate } from '@/app/utils/request/api-routes';
import type { AnswerType } from '~/model/AnswerType';
import type { CommentType } from '~/model/CommentType';
import type { ContributionType } from '~/model/ContributionType';
import type { InteractionType } from '~/model/InteractionType';
import type { PaginationType } from '~/model/PaginationType';
import type { QuestionReconstructedType, QuestionType } from '~/model/QuestionType';
import type { ReactionType } from '~/model/ReactionType';
import { useLocale } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { fetchCollection, fetchItem } from '@/app/actions/server';
import { removeDuplicateItemsById, updateAddItem, updateAddItems, updateAddSubItem, updateAddSubItems, updateReaction } from '@/app/components/Discussion/utils';
import { useAuthenticatedContext } from '@/app/components/Provider';
import { getPaginationNextPage } from '@/app/utils/entities/collection';
import { api, transitionsContribution } from '~/const/api';
import { AcceptLanguage } from '~/const/locale';
import { getIdFromUri } from '../utils/entities/uri';

export function useComments({
  answers: baseAnswers,
  interaction,
  question,
  resultId,
}: {
  answers?: Collection<AnswerType & CommentType>;
  interaction: InteractionType;
  question: QuestionType | QuestionReconstructedType;
  resultId?: number;
}) {
  const { user } = useAuthenticatedContext();

  const locale = useLocale();
  const params = useParams<{ child: string; id: string }>();
  const searchParams = useSearchParams();

  const child = params.child;
  const focusedItem = searchParams.get('c') && Number.parseInt(searchParams.get('c'), 10);
  const focusedSubItem = searchParams.get('sc') && Number.parseInt(searchParams.get('sc'), 10);

  const [processing, setProcessing] = useState(false);
  const [answers, setAnswers] = useState<Array<AnswerType & CommentType>>(baseAnswers?.['hydra:member'] || []);
  const [pagination, setPagination] = useState(baseAnswers?.['hydra:view']);

  const [childId, setChildId] = useState(child);

  const interactionId = interaction.id;
  const forChildren = interaction.for_children;

  const nextPage = getPaginationNextPage(pagination);

  const getAnswers = async (getNewPage = false) => {
    if (!question)
      return;

    setProcessing(true);

    const data = await fetchCollection<AnswerType & CommentType>(
      !getNewPage && nextPage
        ? {
            pathname: nextPage,
            revalidate: 0,
          }
        : {
            pathname: resultId ? `results/${resultId}/comments` : `questions/${question.id}/answers`,
            revalidate: 0,
            searchParams: {
              'exists[parent]': 'false',
              'order[createdAt]': 'DESC',
              ...childId && {
                child: childId,
              },
            },
            unpaginate: true,
          },
    );

    if (data['hydra:description']) {
      return setProcessing(false);
    }

    const newData = updateAddItems(
      !getNewPage ? null : answers,
      data['hydra:member'],
      getNewPage,
    );

    setAnswers(newData);
    setPagination(data['hydra:view']);
    setProcessing(false);
  };

  const getRemainingAnswers = async (
    focused: AnswerType & CommentType,
    id?: string,
  ) => {
    const data = await fetchCollection<AnswerType & CommentType>({
      pathname: resultId
        ? `results/${resultId}/comments`
        : `questions/${question.id}/answers`,
      revalidate: 0,
      searchParams: {
        'exists[parent]': 'false',
        'order[createdAt]': 'ASC',
        ...id && {
          child: id,
        },
      },
    });

    const remaining = data['hydra:member'];
    const dedup = removeDuplicateItemsById([focused, ...remaining]);
    return { collection: dedup, pagination: data['hydra:view'] };
  };

  const applyRemainingAnswers = ({
    collection,
    pagination: paginationData,
  }: {
    collection: AnswerType[] | CommentType[];
    pagination: PaginationType;
  }) => {
    setAnswers(collection);
    setPagination(paginationData);
    setProcessing(false);
  };

  const remainingAnswers = async (
    item: AnswerType | CommentType,
    currentChild?: string,
  ) => {
    const subItem = await fetchItem<AnswerType | CommentType>({
      pathname: `answers/${focusedSubItem}`,
      revalidate: 0,
      searchParams: {
        ...currentChild && {
          child: currentChild,
        },
      },
    });

    if (subItem['hydra:description']) {
      return;
    }

    // the sub comment should have parent
    // abort the focus display if not so
    if (subItem.parent === null || subItem.parent.id !== focusedItem) {
      return await getAnswers();
    }

    const focusedParent: AnswerType | CommentType = {
      ...item,
      comments: [subItem],
      focusedItem: subItem,
    };

    getRemainingAnswers(focusedParent, currentChild).then(
      applyRemainingAnswers,
    );
  };

  const getFocusedAnswer = async () => {
    if (!question)
      return;

    setProcessing(true);

    const item = await fetchItem<AnswerType | CommentType>({
      pathname: `answers/${focusedItem}`,
      revalidate: 0,
    });

    if (item['hydra:description']) {
      return setProcessing(false);
    }

    // the item should not have a parent
    // abort the focus display if so
    if (item.parent !== null)
      return await getAnswers();

    if (focusedSubItem) {
      const createdFor = item?.contribution?.created_for?.['@id'];

      const createdForId = forChildren
        ? createdFor?.split('/').pop()
        : null;

      const itemCreatorChildId = createdFor === user['@id']
        ? null
        : createdForId;

      setChildId(itemCreatorChildId);

      if (itemCreatorChildId) {
        const comment = await fetchItem<AnswerType | CommentType>({
          pathname: `answers/${focusedItem}`,
          revalidate: 0,
          searchParams: {
            child: itemCreatorChildId,
          },
        });
        await remainingAnswers(comment, itemCreatorChildId);
      } else {
        await remainingAnswers(item, itemCreatorChildId);
      }
    } else {
      await getRemainingAnswers(item).then(applyRemainingAnswers);
    }
  };

  const getComments = async (
    ideaUri: string,
    direct_answers_count: number,
    pageUrl?: UriTemplate,
  ): Promise<PaginationType> => {
    if (direct_answers_count) {
      const data = await fetchCollection<AnswerType & CommentType>({
        pathname: pageUrl || resultId
          ? `results/${resultId}/comments`
          : `questions/${question.id}/answers`,
        ...!pageUrl && {
          searchParams: {
            parent: ideaUri,
            'order[createdAt]': 'ASC',
            ...!!childId && {
              child: childId,
            },
          },
        },
        revalidate: 0,
      });

      const newData = updateAddSubItems(
        answers,
        ideaUri,
        data['hydra:member'],
        pageUrl,
      );

      if (newData) {
        setAnswers(newData);
      }

      return data['hydra:view'];
    } else {
      const newData = updateAddSubItems(answers, ideaUri);
      setAnswers(newData);
      return Promise.resolve({});
    }
  };

  const postComment = async (
    as_admin: boolean,
    value: string,
    parent?: number,
  ) => {
    setProcessing(true);
    const pathname = resultId ? 'comments' : 'contributions';

    const contribution = await fetchItem<ContributionType | CommentType>({
      pathname,
      method: 'POST',
      revalidate: 0,
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
      body: {
        ...(resultId
          ? {
              result: `/api/${api.RESULTS}/${resultId}`,
              value,
              asAdmin: forChildren ? false : as_admin,
            }
          : {
              answers: [
                {
                  ...(parent && { parent: `/api/${api.ANSWERS}/${parent}` }),
                  question: `/api/${api.QUESTIONS}/${question.id}`,
                  value_text: value,
                  asAdmin: forChildren ? false : as_admin,
                },
              ],
              interaction: `/api/${api.INTERACTIONS}/${interactionId}`,
              ...(childId && { created_for: `/api/${api.USERS}/${childId}` }),
            }),
      },
    });

    if (contribution['hydra:description']) {
      return setProcessing(false);
    }

    if (resultId) {
      const newData = parent
        ? updateAddSubItem(answers, contribution as CommentType, parent)
        : updateAddItem(answers, contribution);

      if (newData) {
        setAnswers(newData);
      }

      return setProcessing(false);
    }

    const submittedContribution = await fetchItem<ContributionType>({
      pathname: `${pathname}/${contribution.id}`,
      method: 'PUT',
      body: {
        transition: transitionsContribution.submit,
      },
      revalidate: 0,
    });

    if (submittedContribution['hydra:description']) {
      return setProcessing(false);
    }

    const answer = await fetchItem<AnswerType & CommentType>({
      pathname: `answers/${getIdFromUri(submittedContribution.answers[0])}`,
      revalidate: 0,
    });

    if (answer['hydra:description']) {
      return setProcessing(false);
    }

    const newData = parent
      ? updateAddSubItem(answers, answer, parent)
      : updateAddItem(answers, answer);

    if (newData) {
      setAnswers(newData);
    }

    setProcessing(false);
  };

  const postReaction = async (
    ideaId: number,
    commentId: number,
    value: string,
  ) => {
    setProcessing(true);

    const reaction = await fetchItem<ReactionType>({
      pathname: 'reactions',
      method: 'POST',
      body: {
        value,
        answer: `/api/${api.ANSWERS}/${commentId || ideaId}`,
        ...(childId && { created_for: `/api/${api.USERS}/${childId}` }),
      },
    });

    const newData = updateReaction(
      'add',
      answers,
      reaction,
      ideaId,
      commentId,
    );

    if (reaction) {
      setAnswers(newData);
    }

    setProcessing(false);
    return reaction;
  };

  const deleteReaction = async (
    ideaId: number,
    commentId: number,
    reaction: ReactionType,
  ) => {
    setProcessing(true);

    const result = await fetchItem({
      pathname: `reactions/${reaction.id}`,
      method: 'DELETE',
      revalidate: 0,
    });

    if (result['hydra:description']) {
      setProcessing(false);
    }

    const newData = updateReaction(
      'remove',
      answers,
      reaction,
      ideaId,
      commentId,
    );

    setAnswers(newData);
    setProcessing(false);
  };

  useEffect(() => {
    if (focusedItem) {
      getFocusedAnswer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id]);

  return {
    answers,
    deleteReaction,
    getAnswers,
    getComments,
    pagination,
    postComment,
    postReaction,
    processing,
  };
}
